import {useState, useEffect} from 'react';
import { useMediaQuery } from '@mui/material';
import AvgCompletedIcon from '@mui/icons-material/ChecklistRounded';
import CompletedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { ReactComponent as AssignedIcon } from '@Assets/users.svg';
import OverdueIcon from '@mui/icons-material/AvTimerRounded';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip
} from 'recharts';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './ProfileUserCharts.scss';

export default function ProfileUserCharts(props) {
    const [totalCompleted, setTotalCompleted] = useState(0);
    const [totalAssigned, setTotalAssigned] = useState(0);
    const [zeroMonths, setZeroMonths] = useState(0);
    const [averageCompleted, setAverageCompleted] = useState(0);
    const [averageAssigned, setAverageAssigned] = useState(0);
    const [completedThisMonth, setCompletedThisMonth] = useState(0);
    const [assignedThisMonth, setAssignedThisMonth] = useState(0);
    const [timeRange, setTimeRange] = useState(6);
    const [monthlyData, setMonthlyData] = useState([]);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const className = 'profileUserCharts';

    useEffect(() => {
        if(!props.userAnalysisLoader){
            parseData();
            parseMonthly();
        }
    }, [props, timeRange]);

    const monthShort = (date) => {
        const parts = date.toString().split(' ');
        return parts[1] + ' ' + parts[3].substring(2);
    };

    const parseMonthly = () => {
        const monthVariableList = ['One', 'Two', 'Three', 'Four', 'Five', 'Six'];
        const now = new Date();
        const out = [];
        for (let i = 0; i < timeRange; i++) {
            const idx = timeRange - i - 1;
            const monthKey = 'userMonth' + monthVariableList[idx];
            const md = props.userAnalysis?.[monthKey] || {};
            const d = new Date(now.getFullYear(), now.getMonth() - (timeRange - i - 1), 1);
            out.push({
                time: monthShort(d),
                completed: md.completed || 0,
                assigned: md.assigned || 0
            });
        }
        setMonthlyData(out);
    };

    const parseData = () => {
        let monthVariableList = ['One', 'Two', 'Three', 'Four', 'Five', 'Six'];
        let totalAssignedValue = 0;
        let totalCompletedValue = 0;
        let zeroMonthsValue = 0;
        if(!!props.userAnalysis) {
            for(var i = 0; i < 6 ; i++) {
                let monthCompleted = 0;
                let monthAssigned = 0;
                if (!!props.userAnalysis['userMonth' + monthVariableList[6 - i - 1]]) {
                    monthCompleted = props.userAnalysis['userMonth' + monthVariableList[6 - i - 1]]['completed'];
                }
                if(!!props.userAnalysis['userMonth' + monthVariableList[6 - i - 1]]) {
                    monthAssigned = props.userAnalysis['userMonth' + monthVariableList[6 - i - 1]]['assigned'];
                }
                totalCompletedValue += monthCompleted || 0;
                totalAssignedValue += monthAssigned || 0;
                if((monthCompleted || 0) + (monthAssigned || 0) === 0){
                    zeroMonthsValue += 1;
                }
            }
        }
        setCompletedThisMonth(props.userAnalysis?.userMonthOne?.completed || 0);
        setAssignedThisMonth(props.userAnalysis?.userMonthOne?.assigned || 0);
        setTotalCompleted(totalCompletedValue);
        setTotalAssigned(totalAssignedValue);
        setZeroMonths(zeroMonthsValue);
        if(zeroMonthsValue < 6) {
            setAverageCompleted(Number((totalCompletedValue/(6 - zeroMonthsValue)).toFixed(2)));
            setAverageAssigned(Number((totalAssignedValue/(6 - zeroMonthsValue)).toFixed(2)));
        } else {
            setAverageCompleted(0);
            setAverageAssigned(0);
        }
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className={className + "__tooltip"}>
                    <div className={className + "__tooltip__title"}>{label}</div>
                    {payload.map((entry, i) => (
                        <div key={i} className={className + "__tooltip__row"}>
                            <span className={className + "__tooltip__label"} style={{ color: entry.color }}>
                                {entry.name}
                            </span>
                            <span className={className + "__tooltip__value"} style={{ color: entry.color }}>
                                {entry.value}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const ActiveBar = ({ x, y, width, height, fill }) => (
        <g>
            <rect
            x={x}
            y={y - 6}
            width={width}
            height={height + 4}
            fill={fill}
            rx={4}
            ry={4}
            style={{ filter: "drop-shadow(0px 4px 6px var(--color-shadow))", transition: "all 0.25s ease" }}
            />
        </g>
    );

    return (
        <div className={className}>
            {/* Header */}
            <div className={className + '__header'}>
                <div className={className + '__header__left'}>
                    <div className={className + '__title'}>Work Summary</div>
                    {!isMobile && <div className={className + '__subtitle'}>Monthly task completion</div>}
                </div>
                <div className={className + '__header__right'}>
                    {!isMobile && <label htmlFor="usercharts-range" className={className + '__control__label'}>Range</label>}
                    <Select
                        id="usercharts-range"
                        value={timeRange}
                        onChange={(e) => setTimeRange(parseInt(e.target.value, 10))}
                        className={className + '__select'}
                        size="small"
                    >
                        <MenuItem value={6}>Last 6 Months</MenuItem>
                        <MenuItem value={3}>Last 3 Months</MenuItem>
                    </Select>
                </div>
            </div>

            {/* KPI Chips */}
            {props.userAnalysisLoader ? (
                <div className={className + '__kpis ' + className + '__kpis--loading'}>
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className={className + '__kpi'}>
                            <Skeleton variant="circular" width={isMobile ? 48 : 36} height={isMobile ? 48 : 36} />
                            <div className={className + '__kpi__content'}>
                                <Skeleton width={90} height={isMobile ? 16 : 18} />
                                <Skeleton width={60} height={isMobile ? 24 : 22} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className={className + '__kpis'}>
                    <Tooltip title="Average completed per month" arrow placement="top">
                        <div className={className + '__kpi'}>
                            <div className={className + '__kpi__icon kpi--blue'}>
                                <AvgCompletedIcon />
                            </div>
                            <div className={className + '__kpi__content'}>
                                <div className={className + '__kpi__label'}>Avg / Month</div>
                                <div className={className + '__kpi__value'}>{averageCompleted}</div>
                            </div>
                        </div>
                    </Tooltip>
                    <Tooltip title="Completed this month" arrow placement="top">
                        <div className={className + '__kpi'}>
                            <div className={className + '__kpi__icon kpi--green'}>
                                <CompletedIcon />
                            </div>
                            <div className={className + '__kpi__content'}>
                                <div className={className + '__kpi__label'}>This Month</div>
                                <div className={className + '__kpi__value'}>{completedThisMonth}</div>
                            </div>
                        </div>
                    </Tooltip>
                    <Tooltip title="Assigned this month" arrow placement="top">
                        <div className={className + '__kpi'}>
                            <div className={className + '__kpi__icon kpi--indigo'}>
                                <AssignedIcon />
                            </div>
                            <div className={className + '__kpi__content'}>
                                <div className={className + '__kpi__label'}>Assigned</div>
                                <div className={className + '__kpi__value'}>{assignedThisMonth}</div>
                            </div>
                        </div>
                    </Tooltip>
                    <Tooltip title="Currently overdue" arrow placement="top">
                        <div className={className + '__kpi'}>
                            <div className={className + '__kpi__icon kpi--red'}>
                                <OverdueIcon />
                            </div>
                            <div className={className + '__kpi__content'}>
                                <div className={className + '__kpi__label'}>Overdue</div>
                                <div className={className + '__kpi__value'}>{props?.userAnalysis?.userStats?.overdueCount || 0}</div>
                            </div>
                        </div>
                    </Tooltip>
                </div>
            )}

            {/* Chart Card */}
            <div className={className + '__card'}>
                {props.userAnalysisLoader ? (
                    <div className={className + '__loader'}>
                        <LinearProgress className={className + '__loader__bar default-loader'} color="inherit" />
                        <LinearProgress className={className + '__loader__bar default-loader'} color="inherit" />
                        <LinearProgress className={className + '__loader__bar default-loader'} color="inherit" />
                    </div>
                ) : (
                    <div className={className + '__chart'}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyData} margin={{ top: 10, right: isMobile ? 8 : 16, left: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="time"
                                    tick={{ fill: 'var(--color-primaryText)', fontSize: isMobile ? 11 : 12 }}
                                    axisLine={{ stroke: 'var(--color-primaryText)' }}
                                    tickLine={{ stroke: 'var(--color-primaryText)' }}
                                />
                                <YAxis
                                    tick={{ fill: 'var(--color-primaryText)', fontSize: isMobile ? 11 : 12 }}
                                    axisLine={{ stroke: 'var(--color-primaryText)' }}
                                    tickLine={{ stroke: 'var(--color-primaryText)' }}
                                    tickCount={4}
                                />
                                <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                                <Bar dataKey="assigned" name="Assigned" fill="var(--color-primaryGrey)" radius={[4,4,0,0]} activeBar={<ActiveBar />} />
                                <Bar dataKey="completed" name="Completed" fill="var(--color-primaryBlue)" radius={[4,4,0,0]} activeBar={<ActiveBar />} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
